// Number nicknames (Numbers PR 2): how one of the user's own numbers is shown when it
// has a name. The name never replaces the digits where there is room for both:
//   full:  "Sales · +1 (201) 285-8570"   (ringing screen, call alert, Calling from, admin)
//   short: "Sales (8570)"                (history and voicemail rows; full number in a tooltip)
// Without a name both are just the formatted number, as before. App-only: callers
// still see the real number.

import { formatPhone } from './number';

export type NumberNames = Readonly<Record<string, string>> | null | undefined;

export const NICKNAME_MAX = 24;

const digits = (n: string) => n.replace(/[^0-9]/g, '');
/** Same number with or without the leading 1 (10 / 11 digits). */
const key = (n: string) => {
  const d = digits(n);
  return d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
};

/** The name of one of the user's numbers, or null. */
export function nicknameOf(names: NumberNames, number: string | null | undefined): string | null {
  if (!names || !number) return null;
  const k = key(number);
  if (!k) return null;
  for (const [n, name] of Object.entries(names)) if (name && key(n) === k) return name;
  return null;
}

/** "Sales · +1 (201) 285-8570", or the formatted number when it has no name. */
export function fullLabel(number: string, names: NumberNames): string {
  const name = nicknameOf(names, number);
  return name ? `${name} · ${formatPhone(number)}` : formatPhone(number);
}

/** "Sales (8570)", or the formatted number when it has no name. */
export function shortLabel(number: string, names: NumberNames): string {
  const name = nicknameOf(names, number);
  return name ? `${name} (${digits(number).slice(-4)})` : formatPhone(number);
}

/** Same rules as the server (number-names.ts): trimmed, spaces squeezed, '' = remove, ≤ 24. */
export function nicknameProblem(input: string): string | null {
  const name = input.replace(/\s+/g, ' ').trim();
  if ([...name].length > NICKNAME_MAX) return `Keep the name to ${NICKNAME_MAX} characters or fewer.`;
  if (/[\p{Cc}\p{Cf}\p{Co}\p{Cn}]/u.test(name)) return 'Use letters, numbers and normal punctuation only.';
  return null;
}
